import { Button, IconButton } from '@whoop/web-components';
import React from 'react';
import { Control, FieldValues, useFieldArray, useFormContext, RegisterOptions } from 'react-hook-form';
import styles from './index.module.scss';

interface MultiInputProps {
  name: string;
  control: Control<FieldValues, any>;
  min?: number;
  addText?: string;
  label?: (index: number) => string;
  placeholder?: string;
  inputClassName?: string;
  type?: string;
  required?: string | boolean | { value: boolean; message: string };
  pattern?: RegExp | { value: RegExp; message: string };
  minLength?: number | { value: number; message: string };
  maxLength?: number | { value: number; message: string };
  [key: string]: any;
}

function MultiInput({
  name,
  control,
  min = 0,
  placeholder,
  addText = 'Add',
  label,
  inputClassName,
  required,
  pattern,
  minLength,
  maxLength,
  type,
  ...restOfInputProps
}: MultiInputProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const { register, formState: { errors } } = useFormContext();

  return (
    <div>
      {fields.map((field, index) => {
        const fieldName = `${name}.${index}.value` as const;
        const fieldError = (errors[name] as any)?.[index]?.value;
        const fieldErrorMessage = (fieldError as { message?: string })?.message;

        const registerOptions: RegisterOptions = {};
        if (required !== undefined) registerOptions.required = required;
        if (pattern !== undefined) registerOptions.pattern = pattern;
        
        let htmlMinLength: number | undefined;
        if (typeof minLength === 'number') {
          htmlMinLength = minLength;
        } else if (minLength?.value !== undefined) {
          htmlMinLength = minLength.value;
          registerOptions.minLength = minLength;
        } else {
          registerOptions.minLength = minLength;
        }

        let htmlMaxLength: number | undefined;
        if (typeof maxLength === 'number') {
          htmlMaxLength = maxLength;
        } else if (maxLength?.value !== undefined) {
          htmlMaxLength = maxLength.value;
          registerOptions.maxLength = maxLength;
        } else {
            registerOptions.maxLength = maxLength;
        }

        return (
          <div key={field.id} className={styles.inputWrapper}>
            <div className={styles.fieldContentWrapper}>
              {label && (
                <label 
                  htmlFor={fieldName} 
                  className={`${styles.inputLabel} ${fieldErrorMessage ? styles.inputLabelError : ''}`.trim()}
                >
                  {label(index)}
                </label>
              )}
              <input
                id={fieldName}
                type={type || 'text'}
                className={`${styles.formInput} ${fieldErrorMessage ? styles.inputError : ''} ${inputClassName || ''}`.trim()}
                placeholder={placeholder}
                {...register(fieldName, registerOptions)}
                minLength={htmlMinLength}
                maxLength={htmlMaxLength}
                {...restOfInputProps}
              />
              {fieldErrorMessage && (
                <p className={styles.errorMessage}>{fieldErrorMessage}</p>
              )}
            </div>
            {index >= min && (
              <IconButton
                name="trash"
                size="small"
                theme="dark"
                className={styles.whiteIconIconButton}
                onClick={() => remove(index)}
                aria-label={`Remove ${label ? label(index).replace('*', '').trim() : `item ${index + 1}`}`}
              />
            )}
          </div>
        );
      })}
      <Button
        variant="normal"
        theme="dark"
        onClick={() => append({ value: '' })}
      >
        {addText}
      </Button>
    </div>
  );
}

export default MultiInput;
